<template>
  <div
    class="vs-tooltip"
    :dir="direction"
    @focusout="focusout_closeTooltip($event)"
    @keydown="onkeydown_closeTooltipOnEsc($event)"
  >
    <button
      ref="tooltipbutton"
      type="button"
      class="visa-icon visa-icon-info"
      :aria-label="allyOpenLabel"
      @click="onclick_toggleTooltip($event)"
      @mouseenter="onmouseover_toggleTooltip($event)"
    />
    <div
      :id="'id-tooltip-' + computedId"
      ref="tooltipdialog"
      class="vs-tooltip-dialog"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
    >
      <h2
        v-if="tooltipHeader"
        ref="tooltipheader"
        class="vs-lead"
        aria-live="polite"
        tabindex="-1"
      >
        {{ tooltipHeader }}
      </h2>
      <p
        v-if="tooltipBody"
        class="vs-text"
        tabindex="-1"
        v-html="tooltipBody"
      />
      <button
        ref="closebutton"
        type="button"
        class="vs-close"
        :aria-label="allyCloseLabel"
        @click="onclick_closeTooltip($event)"
        @keydown="onkeydown_closeTooltip($event)"
      />
      <slot />
      <i
        aria-hidden="true"
        :class="tooltipArrowClass"
      />
    </div>
    <div
      v-if="show"
      ref="tooltipbackground"
      class="vs-tooltip-background"
      @click="onclick_closeTooltip($event)"
    />
  </div>
</template>

<script>
import { generateId } from '@/helpers/generate-id';
import { setDirection } from '@/helpers/direction';

export default {
  name: 'VsTooltip',
  props: {
    id: { type: String, default: '' },
    direction: { type: String, default: setDirection() },
    tooltipHeader: { type: String, default: '' },
    tooltipBody: { type: String, default: '' },
    allyOpenLabel: { type: String, default: 'Open Tooltip' },
    allyCloseLabel: { type: String, default: 'Close Tooltip' },
  },
  data() {
    return {
      show: false,
      isMobile: false,
      tooltipArrowClass: '',
      computedId: '',
    };
  },
  mounted() {
    this.onResize();
    window.addEventListener('resize', this.onResize);
    this.computedId = this.id.length === 0 ? generateId().toString() : this.id;
  },
  unmounted() {
    window.removeEventListener('resize', this.onResize);
  },
  methods: {
    onResize() {
      this.isMobile = window.innerWidth < 768;
    },
    close(dontReturnFocus) {
      this.show = !this.show;
      this.$refs.tooltipbackground.style.background = 'transparent';
      this.$refs.tooltipdialog.style.display = 'none';
      if (!dontReturnFocus) {
        this.$refs.tooltipbutton.focus();
      }
    },
    open() {
      let innerWidth = window.innerWidth;
      this.show = !this.show;
      this.$refs.tooltipdialog.style.display = 'block';
      this.$refs.tooltipdialog.style.zIndex = '10';
      this.$refs.tooltipheader.focus();

      if (!this.isMobile) {
        let dialogPosition = this.$refs.tooltipdialog.getBoundingClientRect();
        let buttonPosition = this.$refs.tooltipbutton.getBoundingClientRect();
        let rightPosition = innerWidth - buttonPosition.right;
        let x = -(dialogPosition.width / 2) + buttonPosition.width / 2 + 5;
        let y = -105;

        let isAbove = true;

        // 0-left, 1-center, 2-right
        let horizontalPosition = 1;

        // Determine above/below

        // tooltip above button (adjusting based on tooltip dialog height)
        if (dialogPosition.height > 100) {
          y = y - (dialogPosition.height - 100);
        }

        //tooltip below button
        if (buttonPosition.top < dialogPosition.height) {
          y = buttonPosition.height + 10;
          isAbove = false;
        }

        this.$refs.tooltipdialog.style.top = `${y}px`;

        // Determine left/right/center

        // Left aligned
        if (buttonPosition.left < dialogPosition.width / 2) {
          x = 5;
          horizontalPosition = 0;
        }

        // Right aligned
        if (rightPosition < dialogPosition.width / 2) {
          x =
            -(dialogPosition.width - rightPosition) +
            buttonPosition.width / 2 +
            15;
          horizontalPosition = 2;
        }

        this.tooltipArrowClass = this.findTooltipArrowPosition(
          isAbove,
          horizontalPosition
        );

        this.$refs.tooltipdialog.style.left = `${x}px`;
        this.$refs.tooltipdialog.style.position = 'absolute';
      } else {
        this.$refs.tooltipdialog.style.position = 'fixed';
        this.$refs.tooltipdialog.style.left = '50%';
        this.$refs.tooltipdialog.style.top = '50%';
      }
    },
    findTooltipArrowPosition(isAbove, horizontalPosition) {
      if (isAbove && horizontalPosition == 0) {
        return 'vs-bottom-left';
      } else if (isAbove && horizontalPosition == 1) {
        return '';
      } else if (isAbove && horizontalPosition == 2) {
        return 'vs-bottom-right';
      } else if (!isAbove && horizontalPosition == 0) {
        return 'vs-top-left';
      } else if (!isAbove && horizontalPosition == 1) {
        return 'vs-top-center';
      } else if (!isAbove && horizontalPosition == 2) {
        return 'vs-top-right';
      }
    },
    onclick_toggleTooltip() {
      if (this.show) {
        this.close();
      } else {
        this.open();
      }
    },
    onmouseover_toggleTooltip() {
      if (!this.show) {
        this.open();
      }
    },
    onclick_closeTooltip() {
      this.close();
    },
    onkeydown_closeTooltip(e) {
      this.onkeydown_closeTooltipOnEsc(e);
      const keyCode = e.code;
      switch (keyCode) {
        case 'Tab':
          if (!e.shiftKey) {
            this.close();
          }
          break;
        default:
          break;
      }
    },
    onkeydown_closeTooltipOnEsc(e) {
      const keyCode = e.code;
      switch (keyCode) {
        case 'Esc':
        case 'Escape':
          this.close();
          break;
        default:
          break;
      }
    },
    focusout_closeTooltip(e) {
      if (
        (!e.relatedTarget || !this.$el.contains(e.relatedTarget)) &&
        this.show
      ) {
        this.close(true);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './tooltip';
@import '@/styles/core/types/types';
@import '@/styles/buttons/buttons';
@import '@/styles/input';
@import '@/styles/label';
@import '@/styles/accessibility';
</style>
